import { useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { interviews, candidates } from '@/data/mockData';
import type { Candidate } from '@/data/mockData';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Calendar, Clock, Video, Phone, Users, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';

const InterviewsPage = () => {
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [interviewType, setInterviewType] = useState('video');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');

  const handleSchedule = () => {
    if (!selectedCandidate || !date || !time) {
      toast.error('Please fill in all required fields');
      return;
    }

    const candidate = candidates.find((c) => c.id === selectedCandidate);
    toast.success(`Interview scheduled with ${candidate?.name} on ${date} at ${time}`);
    setSelectedCandidate('');
    setDate('');
    setTime('');
    setNotes('');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold">Schedule Interviews</h1>
          <p className="text-muted-foreground">
            Schedule and manage interviews with candidates.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Schedule New Interview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Schedule New Interview
              </CardTitle>
              <CardDescription>
                Select a candidate and set up the interview details.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Candidate Selection */}
              <div className="space-y-2">
                <Label>Select Candidate</Label>
                <Select value={selectedCandidate} onValueChange={setSelectedCandidate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a candidate" />
                  </SelectTrigger>
                  <SelectContent>
                    {candidates.map((candidate) => (
                      <SelectItem key={candidate.id} value={candidate.id}>
                        <span className="flex items-center gap-2">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                            {candidate.name.split(' ').map((n) => n[0]).join('')}
                          </span>
                          {candidate.name} - {candidate.title}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Interview Type */}
              <div className="space-y-2">
                <Label>Interview Type</Label>
                <Select value={interviewType} onValueChange={setInterviewType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">
                      <span className="flex items-center gap-2">
                        <Video className="h-4 w-4" /> Video Call
                      </span>
                    </SelectItem>
                    <SelectItem value="phone">
                      <span className="flex items-center gap-2">
                        <Phone className="h-4 w-4" /> Phone Call
                      </span>
                    </SelectItem>
                    <SelectItem value="in-person">
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4" /> In-Person
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time">Time</Label>
                  <Input
                    id="time"
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any notes for the interview..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <Button className="w-full" onClick={handleSchedule}>
                Schedule Interview
              </Button>
            </CardContent>
          </Card>

          {/* Upcoming Interviews */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Upcoming Interviews
              </CardTitle>
              <CardDescription>
                Your scheduled interviews with candidates.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {interviews.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Calendar className="h-12 w-12 text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">No interviews scheduled</p>
                </div>
              ) : (
                interviews.map((interview) => (
                  <div
                    key={interview.id}
                    className="flex items-start justify-between rounded-lg border border-border p-4 transition-all hover:border-primary/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                        {interview.candidateName.split(' ').map((n) => n[0]).join('')}
                      </div>
                      <div>
                        <p className="font-medium">{interview.candidateName}</p>
                        <p className="text-sm text-muted-foreground">{interview.position}</p>
                        <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(interview.date).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {interview.time}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Badge variant="secondary" className="flex items-center gap-1">
                      {interview.type === 'video' && <Video className="h-3 w-3" />}
                      {interview.type === 'phone' && <Phone className="h-3 w-3" />}
                      {interview.type === 'in-person' && <Users className="h-3 w-3" />}
                      {interview.type}
                    </Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InterviewsPage;
